<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import Modal from "@/ui/components/modal.vue";
import useDialog from "@/ui/composables/useDialog";
import { useBasePriceStore, BasePriceBook } from "@/modules/base_price/store";

const store = useBasePriceStore();
const visible = defineModel<boolean>("visible", { default: false });

/**
 * The editing surface for the book set. Every change is pushed straight to the
 * store, which re-resolves on its own debounce, so Book size and Book CPM
 * answer while you type — an impressions carve-out changes what a book has to
 * sell, and its CPM with it, which is the whole reason to be typing a number
 * here.
 *
 * Nothing is held back, so there is nothing to commit and nothing to revert:
 * closing the dialog only closes it. An earlier version buffered the edits and
 * applied them on Save, which left those two columns showing the previous
 * resolve and then shut before the new one landed, so a change looked like it
 * had done nothing.
 */
const draft = reactive<BasePriceBook[]>([]);

/** Suppresses the commit watcher while seeding, so opening the dialog is not itself an edit. */
const seeding = ref(false);

/**
 * Seeds from the working book set, which is also what every edit here writes
 * back to. Re-read on each open so the dialog reflects anything that changed
 * while it was shut — a book removed from elsewhere, or figures moved by a
 * different action.
 */
const seed = () => {
	seeding.value = true;

	draft.splice(0, draft.length, ...store.books.map((book) => ({ ...book })));

	// Lets the deep watcher see this mutation and skip it before re-arming.
	void Promise.resolve().then(() => (seeding.value = false));
};

watch(visible, (open) => open && seed(), { immediate: true });

const COMMIT_DELAY_MS = 500;

let commitTimer: ReturnType<typeof setTimeout> | undefined;

const commit = () => {
	commitTimer = undefined;
	store.setDraftBooks(draft.map((book) => ({ ...book })));
};

/**
 * Held for half a second so a name typed letter by letter is one write rather
 * than one per keystroke.
 *
 * The store debounces the *resolve* it schedules, but not the write itself —
 * and that write replaces the book set, prunes the overrides against it and
 * re-checks the selected book, waking every computed reading `books`. Typing a
 * name was doing all of that per character to change a string nothing
 * calculates from.
 */
const scheduleCommit = () => {
	clearTimeout(commitTimer);
	commitTimer = setTimeout(commit, COMMIT_DELAY_MS);
};

/** Applies a pending edit immediately — closing must not lose the last half second of typing. */
const flushCommit = () => {
	if (commitTimer === undefined) return;

	clearTimeout(commitTimer);
	commit();
};

// Any change to any book — a name, a percentage, a toggle, an add or a
// remove — reaches the store.
watch(
	draft,
	() => {
		if (seeding.value) return;

		scheduleCommit();
	},
	{ deep: true }
);

onBeforeUnmount(flushCommit);

const openInventory = computed(() =>
	draft.find((book) => book.type === "open_inventory")
);

const reservations = computed(() =>
	draft.filter((book) => book.type === "reservation")
);

const randomUuid = (): string =>
	crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const addBook = () => {
	if (!store.plan) return;

	draft.push({
		uuid: randomUuid(),
		plan_uuid: store.plan.uuid,
		type: "reservation",
		name: "New reservation",
		is_enabled: true,
		sort_order: draft.length,
		budget_percentage: 5,
		impression_percentage: 5,
	} as BasePriceBook);
};

const removeBook = (book: BasePriceBook) => {
	const index = draft.indexOf(book);
	if (index >= 0) draft.splice(index, 1);
};

const setNumber = (
	book: BasePriceBook,
	field: "budget_percentage" | "impression_percentage",
	value: number
) => {
	book[field] = Math.min(100, Math.max(0, value));
};

/**
 * What a reservation book takes off the target. Still read from the draft
 * rather than the resolver: budget follows straight from the percentage, so
 * it can answer instantly instead of waiting on the round-trip that the
 * impression-driven figures beside it genuinely need.
 */
const bookBudget = (book: BasePriceBook): number =>
	book.is_enabled
		? ((store.plan?.target ?? 0) * (book.budget_percentage ?? 0)) / 100
		: 0;

const reservedBudget = computed(() =>
	reservations.value.reduce((sum, book) => sum + bookBudget(book), 0)
);

const openBudget = computed(
	() => (store.plan?.target ?? 0) - reservedBudget.value
);

/**
 * Impressions and CPM are the resolver's domain — neither follows from a
 * percentage alone, since a book reserves a share of every screen's own gross
 * and its CPM is budget ÷ what that leaves it to sell. They catch up a moment
 * after the edit, on the store's resolve debounce.
 */
const resolvedBook = (book: BasePriceBook) => store.resolvedBook(book);

const overCommitted = computed(
	() => reservedBudget.value > (store.plan?.target ?? 0)
);

/**
 * "Impressions reserved" is only a *default*: ImpressionShareResolver walks up
 * from each leaf and takes the first impression-share override it finds, and
 * only falls back to this number when it finds none. An override on the book's
 * root node therefore shadows it completely — the field stays editable but
 * changes nothing, which reads as a broken input unless it says so.
 */
const impressionShareOverrides = (book: BasePriceBook) =>
	store.draftOverrides.filter(
		(override) =>
			override.book_uuid === book.uuid && override.type === "impression_share"
	);

const shadowedAtRoot = (book: BasePriceBook): boolean => {
	const rootId = store.resolvedBook(book)?.nodes
		? Object.values(store.resolvedBook(book)!.nodes).find(
				(node) => node.parent_id === null
		  )?.hierarchy_node_id
		: undefined;

	return (
		rootId !== undefined &&
		impressionShareOverrides(book).some(
			(override) => override.hierarchy_node_id === rootId
		)
	);
};

const impressionShareNote = (book: BasePriceBook): string | null => {
	if (!book.is_enabled) return null;

	// Only the case where the default does nothing at all is worth saying.
	// Partial shadowing further down still leaves this number in charge
	// everywhere else, which is ordinary inheritance, not a caveat.
	return shadowedAtRoot(book)
		? "Not in use — this book sets its own impression share on the whole hierarchy, so this default never applies. Change it on the book's Impression share tab."
		: null;
};

const bookNote = (book: BasePriceBook): string => {
	if (!book.is_enabled) {
		return "Switched off — excluded from the model, and its impressions return to open inventory.";
	}

	const hasOverrides = store.draftOverrides.some(
		(override) => override.book_uuid === book.uuid
	);

	return hasOverrides
		? "Its own split and monthly budget distribution live in this book. Overrides set inside."
		: "Its own split and monthly budget distribution live in this book. No overrides yet — everything inherits.";
};

/** Everything is already applied; Save only closes and keeps it. */
type EmptyMonthTarget = {
	nodeId: number | null;
	assetId: number | null;
	shares: number[];
};

/**
 * Per book, every target whose inventory doesn't run all year. A month with no
 * impressions still receives its slice of the budget from the seasonality
 * shape, and nothing can be sold against it — so that slice is simply never
 * earned, and the level quietly reports revenue below budget with no override
 * anywhere to explain it.
 *
 * Computed once for every book rather than per row: the template asks for this
 * on each render, and walking a few thousand screens per book per render is
 * the kind of thing that made this action unusable the first time round.
 */
const emptyMonthTargetsByBook = computed<Map<string, EmptyMonthTarget[]>>(
	() => {
		const byBook = new Map<string, EmptyMonthTarget[]>();

		draft.forEach((book) => {
			const resolved = store.resolvedBook(book);
			if (!resolved) return;

			const targets: EmptyMonthTarget[] = [];

			/**
			 * A target qualifies when it has an empty month *and* a month with
			 * inventory — an all-empty screen has no shape to follow — and when the
			 * shape it would get differs from the one it already has. Skipping
			 * no-ops matters: on a large portfolio most targets already follow their
			 * own impressions, and writing an identical override for each is pure
			 * payload.
			 */
			const consider = (
				nodeId: number | null,
				assetId: number | null,
				monthly: number[],
				current: number[]
			) => {
				if (!monthly.some((value) => !value)) return;

				const total = monthly.reduce((sum, value) => sum + value, 0);
				if (total <= 0) return;

				const shares = monthly.map(
					(value) => Math.round((value / total) * 1000) / 10
				);

				if (
					shares.every((share, i) => Math.abs(share - (current[i] ?? 0)) < 0.05)
				) {
					return;
				}

				targets.push({ nodeId, assetId, shares });
			};

			(resolved.nodes ? Object.values(resolved.nodes) : []).forEach((node) => {
				consider(
					node.hierarchy_node_id,
					null,
					node.monthly_gross_impressions,
					node.monthly_season_shares
				);

				store
					.assetsOf(node)
					.forEach((asset) =>
						consider(
							null,
							asset.asset_id,
							asset.monthly_gross_impressions,
							asset.monthly_season_shares
						)
					);
			});

			byBook.set(book.uuid, targets);
		});

		return byBook;
	}
);

const emptyMonthTargets = (book: BasePriceBook): EmptyMonthTarget[] =>
	emptyMonthTargetsByBook.value.get(book.uuid) ?? [];

const emptyMonthHint = (book: BasePriceBook): string =>
	`Give every level with empty months a monthly budget distribution that follows its own impressions (${
		emptyMonthTargets(book).length
	})`;

/**
 * Gives each of those targets a seasonality that follows its own inventory, so
 * a month with nothing to sell is allocated nothing to sell it for.
 *
 * Deliberately only touches targets that have a gap: a shape that follows
 * impressions is a reasonable default but a poor thing to impose on a level
 * whose seasonality someone chose on purpose.
 */
const followImpressionsWhereEmpty = async (book: BasePriceBook) => {
	const targets = emptyMonthTargets(book);
	if (!targets.length) return;

	const confirmed = await useDialog().confirm({
		title: "Follow impressions where months are empty?",
		message:
			`${targets.length} level${targets.length === 1 ? "" : "s"} in ${
				book.name
			} ` +
			`${
				targets.length === 1 ? "has" : "have"
			} months with no impressions.\n\n` +
			"Each will get a monthly budget distribution shaped like its own impressions, " +
			"so those months are allocated no budget. Any distribution already set " +
			"on them is replaced. Everything else, and every other book, is left " +
			"alone.",
		confirmButtonText: "Apply",
	});

	if (!confirmed) return;

	// One batched write and one resolve, rather than one of each per target —
	// see setDraftOverrides().
	store.setDraftOverrides(
		targets.map((target) => ({
			bookUuid: book.uuid,
			hierarchyNodeId: target.nodeId,
			type: "season",
			data: {
				monthly_shares: target.shares,
				locked_months: Array(12).fill(false),
			},
			assetId: target.assetId ?? undefined,
		}))
	);
};

const close = () => {
	flushCommit();
	visible.value = false;
};
</script>

<template>
	<modal
		v-model:visible="visible"
		title="Budget Books"
		icon="fa-light fa-gear"
		width="60rem"
		:persistent="false"
	>
		<!--
			The editor's own progress bar is behind this dialog, and this is where
			resolves are most often triggered — every keystroke on a carve-out
			re-prices the whole plan — so it needs its own.
		-->
		<v-progress-linear
			:active="store.resolving"
			indeterminate
			color="primary"
			height="2"
			class="mb-2"
		/>

		<div class="text-body-2 text-medium-emphasis mb-4" style="max-width: 640px">
			Reservation books are carved out of the target before open inventory is
			priced. Each one holds its own budget, its own hierarchy split and its own
			seasonality, editable by opening it.
		</div>

		<div class="d-flex flex-column ga-3">
			<v-card
				v-for="book in reservations"
				:key="book.uuid"
				variant="outlined"
				class="pa-4"
			>
				<!--
					One labelled cell per column, all sharing the same label style
					and the same 40px control row, so the labels line up and a
					read-out sits on the same line as the field beside it.
				-->
				<div class="d-flex align-start ga-3 flex-wrap">
					<div class="flex-grow-1" style="min-width: 140px">
						<div class="book-label">Name</div>
						<v-text-field
							v-model="book.name"
							density="compact"
							variant="outlined"
							hide-details
							placeholder="Book name"
							class="book-row-field"
						/>
					</div>

					<div>
						<div class="book-label">Budget carve-out</div>
						<v-number-input
							:model-value="book.budget_percentage"
							control-variant="split"
							inset
							:min="0"
							:max="100"
							:step="1"
							:precision="1"
							suffix="%"
							density="compact"
							variant="outlined"
							hide-details
							class="book-row-field"
							style="width: 124px"
							@update:model-value="
								setNumber(book, 'budget_percentage', Number($event))
							"
						/>
					</div>

					<div>
						<div class="book-label">Impressions reserved</div>
						<v-number-input
							:model-value="book.impression_percentage"
							control-variant="split"
							inset
							:min="0"
							:max="100"
							:step="1"
							:precision="1"
							suffix="%"
							density="compact"
							variant="outlined"
							hide-details
							:class="[
								'book-row-field',
								{ 'book-field--shadowed': shadowedAtRoot(book) },
							]"
							style="width: 124px"
							@update:model-value="
								setNumber(book, 'impression_percentage', Number($event))
							"
						/>
					</div>

					<div style="min-width: 124px">
						<div class="book-label">Book size</div>
						<div class="book-control book-value">
							<template v-if="book.is_enabled">
								{{ formatNumberForScale("money", bookBudget(book)) }}
								<span v-if="resolvedBook(book)" class="text-medium-emphasis">
									&nbsp;·
									{{
										formatNumberForScale(
											"number_no_fraction",
											resolvedBook(book)!.gross_impressions
										)
									}}
									imp
								</span>
							</template>
							<span v-else class="text-medium-emphasis">Switched off</span>
						</div>
					</div>

					<div style="min-width: 68px">
						<div class="book-label">Book CPM</div>
						<div class="book-control book-value">
							{{
								book.is_enabled && resolvedBook(book)
									? formatNumber(resolvedBook(book)!.cpm, "money")
									: "—"
							}}
						</div>
					</div>

					<div>
						<div class="book-label">&nbsp;</div>
						<div class="book-control ga-1">
							<v-btn
								size="small"
								variant="text"
								:disabled="!emptyMonthTargets(book).length"
								:title="emptyMonthHint(book)"
								@click="followImpressionsWhereEmpty(book)"
							>
								<i class="fa-light fa-chart-column mr-1" />
								{{ emptyMonthTargets(book).length }}
							</v-btn>
							<v-btn
								icon="fa: fa-light fa-trash-can"
								size="x-small"
								variant="text"
								color="error"
								title="Remove this book"
								@click="removeBook(book)"
							/>
						</div>
					</div>
				</div>

				<div class="text-caption text-medium-emphasis mt-2">
					{{ bookNote(book) }}
				</div>
				<div
					v-if="impressionShareNote(book)"
					class="text-caption mt-1"
					:class="
						shadowedAtRoot(book) ? 'text-warning' : 'text-medium-emphasis'
					"
				>
					<i class="fa-light fa-triangle-exclamation mr-1" />
					{{ impressionShareNote(book) }}
				</div>
			</v-card>

			<div
				v-if="!reservations.length"
				class="text-body-2 text-medium-emphasis py-4"
			>
				No reservation books yet — the whole target goes to open inventory.
			</div>
		</div>

		<div class="d-flex align-center ga-4 mt-4">
			<v-btn
				size="small"
				variant="tonal"
				prepend-icon="fa-light fa-plus"
				@click="addBook"
			>
				Add new reservation book
			</v-btn>
		</div>

		<v-card variant="outlined" class="pa-4 mt-5">
			<div class="book-label text-uppercase mb-3">
				Left for {{ openInventory?.name ?? "open inventory" }}
			</div>
			<div class="d-flex ga-8 flex-wrap align-start justify-space-between">
				<div class="d-flex ga-8 flex-wrap">
					<div>
						<div class="book-label">Open budget</div>
						<div class="book-value" :class="{ 'text-error': overCommitted }">
							{{ formatNumberForScale("money", openBudget) }}
						</div>
					</div>
					<div>
						<div class="book-label">Reserved budget</div>
						<div class="book-value text-warning">
							{{ formatNumberForScale("money", reservedBudget) }}
						</div>
					</div>
					<div v-if="openInventory && resolvedBook(openInventory)">
						<div class="book-label">Open impressions</div>
						<div class="book-value">
							{{
								formatNumberForScale(
									"number_no_fraction",
									resolvedBook(openInventory)!.gross_impressions
								)
							}}
						</div>
					</div>
				</div>

				<v-btn
					v-if="openInventory"
					size="small"
					variant="text"
					:disabled="!emptyMonthTargets(openInventory).length"
					:title="emptyMonthHint(openInventory)"
					@click="followImpressionsWhereEmpty(openInventory)"
				>
					<i class="fa-light fa-chart-column mr-1" />
					{{ emptyMonthTargets(openInventory).length }}
				</v-btn>
			</div>
			<div v-if="overCommitted" class="text-caption text-error mt-3">
				The reservation books carve out more than the whole target — open
				inventory would be left with nothing.
			</div>
		</v-card>

		<template v-slot:actions>
			<v-spacer />
			<v-btn variant="text" @click="close">Close</v-btn>
		</template>
	</modal>
</template>

<style scoped lang="scss">
// One label style and one control height for every column, so nothing in the
// row sets its own type scale.
.book-label {
	font-size: 12px;
	line-height: 16px;
	color: rgba(var(--v-theme-on-surface), 0.6);
	margin-bottom: 4px;
	white-space: nowrap;
}

// Matches a density="compact" Vuetify field, so a read-out lines up with the
// inputs beside it instead of floating above them.
.book-control {
	display: flex;
	align-items: center;
	min-height: 40px;
}

.book-value {
	font-size: 14px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}

// The row packs six columns and two actions onto one line, so the fields run a
// step below Vuetify's default — matched across the text and number inputs so
// the name doesn't sit larger than the figures beside it.
.book-row-field :deep(input) {
	font-size: 12.5px;
}

.book-row-field :deep(.v-field__append-inner),
.book-row-field :deep(.v-field__prepend-inner) {
	font-size: 12.5px;
}

// The split stepper gives each of − and + a full-height button at Vuetify's
// default width, which on this row costs more than the value they surround.
// Narrowed to what the glyph needs, on both sides, so the number keeps the
// space instead.
.book-row-field :deep(.v-number-input__control .v-btn) {
	width: 24px;
	min-width: 24px;
}

.book-row-field :deep(.v-number-input__control .v-btn .v-icon) {
	font-size: 14px;
}

// Marks the field whose value is entirely shadowed by an override inside the
// book — see impressionShareNote().
.book-field--shadowed :deep(.v-field__outline) {
	--v-field-border-opacity: 1;

	color: rgb(var(--v-theme-warning));
}
</style>
